/**
 * Mode Salin Buku's state (DISCUSSION-buku): the book context per parish and register,
 * persisted per device (which volume, which page, the next number), the focus-mode flag
 * (Meja Bersih, persisted), and the session's tally per parish and register (how many
 * entries this sitting, the last number, the last five rows for the "Baru dimasukkan"
 * strip), which starts afresh with every launch. Data never lives here; the entries are in
 * SQLite behind the worker.
 *
 * Plan 10: an admin keuskupan copies in more than one parish, so the place and the tally are
 * kept under the parish (its id) as well as the register: one parish's book, page and number
 * never open in another's register. A place stored before then names no parish and cannot be
 * placed; it is dropped (version 1), and the book's own latest volume, page and next number
 * open instead.
 */
import type { TabelRegister } from '@satu/shared'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface KonteksTersimpan {
  buku: string
  halaman: number | null
  nomor: number
}

export interface BaruEntri {
  id: string
  nomor: number
  /** "Yufuwai, Yohanes" (or "Pria · Wanita"). */
  judul: string
  /** "3 Sep 2026 · RP A. Sowada, OSC". */
  keterangan: string
}

export interface SesiSalin {
  jumlah: number
  terakhir: number | null
  /** Newest first, at most five. */
  baru: BaruEntri[]
}

export const BARU_MAKS = 5

/** One parish's registers (the key of the outer record is the parish's id). */
export type PerRegister<T> = Partial<Record<TabelRegister, T>>

export interface SalinState {
  konteks: Record<string, PerRegister<KonteksTersimpan>>
  setKonteks(parokiId: string, tabel: TabelRegister, konteks: KonteksTersimpan): void
  mejaBersih: boolean
  setMejaBersih(on: boolean): void
  sesi: Record<string, PerRegister<SesiSalin>>
  catatTersimpan(parokiId: string, tabel: TabelRegister, entri: BaruEntri): void
}

export const SESI_KOSONG: SesiSalin = { jumlah: 0, terakhir: null, baru: [] }

export const useSalin = create<SalinState>()(
  persist(
    (set) => ({
      konteks: {},
      setKonteks: (parokiId, tabel, konteks) =>
        set((state) => ({
          konteks: {
            ...state.konteks,
            [parokiId]: { ...state.konteks[parokiId], [tabel]: konteks },
          },
        })),
      mejaBersih: false,
      setMejaBersih: (mejaBersih) => set({ mejaBersih }),
      sesi: {},
      catatTersimpan: (parokiId, tabel, entri) =>
        set((state) => {
          const paroki = state.sesi[parokiId] ?? {}
          const lama = paroki[tabel] ?? SESI_KOSONG
          return {
            sesi: {
              ...state.sesi,
              [parokiId]: {
                ...paroki,
                [tabel]: {
                  jumlah: lama.jumlah + 1,
                  terakhir: entri.nomor,
                  baru: [entri, ...lama.baru.filter((b) => b.id !== entri.id)].slice(0, BARU_MAKS),
                },
              },
            },
          }
        }),
    }),
    {
      // Legacy storage name from SIMU; kept so devices keep their data (pinned: test/nama-warisan.test.ts).
      name: 'simu:salin',
      version: 1,
      partialize: (state) => ({ konteks: state.konteks, mejaBersih: state.mejaBersih }),
      // Version 0 kept one place per register with no parish: dropped, Meja Bersih kept.
      migrate: (lama) => {
        const mejaBersih =
          typeof lama === 'object' && lama !== null && 'mejaBersih' in lama
            ? lama.mejaBersih === true
            : false
        return { konteks: {}, mejaBersih } as unknown as SalinState
      },
    },
  ),
)
